import React, { useMemo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tasksService, type TaskActivityItem, type TaskApi } from '../../../services/tasksService';
import ChangeDetails, { readFieldChanges, type FieldChange } from '../../common/ChangeDetails/ChangeDetails';
import TaskDeadlineRequests from './TaskDeadlineRequests';
import TaskWorkResults from './TaskWorkResults';
import TaskSubtasks from './TaskSubtasks';
import { userActivityService } from '../../../services/userActivityService';
import { invalidateTaskRelatedQueries } from '../../../utils/invalidateTaskCaches';
import { useProfile } from '../../../hooks/useProfile';
import { hasProfilePermission } from '../../../utils/calendarShared';
import PageLoader from '../../common/PageLoader';
import TaskCompleteConfirmModal from '../TaskCompleteConfirmModal/TaskCompleteConfirmModal';
import DeleteConfirmModal from '../DeleteConfirmModal/DeleteConfirmModal';
// @ts-ignore
import pdfMake from 'pdfmake/build/pdfmake';
// @ts-ignore
import pdfFonts from 'pdfmake/build/vfs_fonts';
import './TaskModal.css';

// Настройка шрифтов для pdfmake
if (pdfFonts && (pdfFonts as any).pdfMake) {
  (pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;
} else if (pdfFonts && (pdfFonts as any).vfs) {
  (pdfMake as any).vfs = (pdfFonts as any).vfs;
}

function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const n1 = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (n1 > 1 && n1 < 5) return forms[1];
  if (n1 === 1) return forms[0];
  return forms[2];
}

/** ISO для обратного отсчёта: из API или из dd.mm.yyyy[ hh:mm] (как в deadline_formatted). */
function effectiveDeadlineIsoFromDetail(detail: TaskApi | undefined): string | null | undefined {
  if (!detail) return undefined;
  if (detail.deadline && String(detail.deadline).trim() !== '') {
    return detail.deadline;
  }
  const raw =
    (detail.deadline_formatted && detail.deadline_formatted.trim()) ||
    (detail.due_date_display && detail.due_date_display.replace(/^До\s+/i, '').trim()) ||
    '';
  if (!raw) return undefined;
  const m = /^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2}))?$/.exec(raw.trim());
  if (!m) return undefined;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  const y = parseInt(m[3], 10);
  const hh = m[4] != null ? parseInt(m[4], 10) : 23;
  const min = m[5] != null ? parseInt(m[5], 10) : 59;
  const dt = new Date(y, mo, d, hh, min, 0, 0);
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt.toISOString();
}

/** Строка для блока «До конца дедлайна» в шапке модалки */
function formatDeadlineCountdown(deadlineIso: string | null | undefined, status: string): string {
  if (!deadlineIso) return 'Не указан';
  if (status === 'completed') return 'Задача завершена';
  if (status === 'cancelled') return 'Задача отменена';
  const end = new Date(deadlineIso);
  if (Number.isNaN(end.getTime())) return 'Не указан';
  const diff = end.getTime() - Date.now();
  if (diff <= 0) return 'Срок истёк';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${pluralRu(days, ['день', 'дня', 'дней'])}`);
  if (hours > 0) parts.push(`${hours} ${pluralRu(hours, ['час', 'часа', 'часов'])}`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes} ${pluralRu(minutes, ['минута', 'минуты', 'минут'])}`);
  return parts.join(' ');
}

function priorityLabel(priority: string): string {
  return priority === 'high' ? 'Повышенный' : 'Обычный';
}

function getCurrentUserId(): number | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const idNum = Number(parsed?.id);
    return Number.isFinite(idNum) ? idNum : null;
  } catch {
    return null;
  }
}

export interface Task {
  id: number;
  name: string;
  assignmentDate: string;
  dueDate: string;
  assigner: string;
  executor: string;
}

type TaskModalTab = 'overview' | 'subtasks' | 'participants' | 'history' | 'review' | 'submit' | 'deadline' | 'results';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  /** С какой вкладки открыть модалку (например «Сдать задачу» из списка) */
  initialTab?: TaskModalTab;
  initialActivityId?: number;
  projectName?: string;
  projectId?: number;
  clientName?: string;
  clientId?: number;
  caseName?: string;
  caseIdentifier?: string;
  caseId?: number;
}

interface Participant {
  id: number;
  name: string;
  avatar?: string;
}

interface HistoryEntry {
  id: number;
  author: Participant;
  status: string;
  comment?: string;
  date: string;
  time: string;
  attachments?: Array<{ name: string; size: string }>;
  changes?: FieldChange[];
}

const TaskModal: React.FC<TaskModalProps> = ({ 
  isOpen, 
  onClose,
  task,
  initialTab = 'overview',
  initialActivityId,
  projectName = '',
  projectId,
  clientName = '',
  clientId,
  caseName = '',
  caseIdentifier = '',
  caseId
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const canEditTask = hasProfilePermission(profile, 'task_edit');
  const canCompleteTask = hasProfilePermission(profile, 'task_complete');
  const [activeTab, setActiveTab] = React.useState<TaskModalTab>('overview');
  const [acceptingTask, setAcceptingTask] = React.useState(false);
  const [submitSending, setSubmitSending] = React.useState(false);
  const [reviewCommentDraft, setReviewCommentDraft] = React.useState('');
  const [reviewBusy, setReviewBusy] = React.useState<{
    performerId: number;
    action: 'accept' | 'needs_revision';
  } | null>(null);
  const [completeConfirmOpen, setCompleteConfirmOpen] = React.useState(false);
  const [completeSending, setCompleteSending] = React.useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = React.useState(false);
  const [archiveSending, setArchiveSending] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteSending, setDeleteSending] = React.useState(false);
  const [workDescription, setWorkDescription] = React.useState('');
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
  const [submitWorkError, setSubmitWorkError] = React.useState<string | null>(null);
  const [deadlineTick, setDeadlineTick] = React.useState(0);
  const [modalOpt, setModalOpt] = useState<{ favorite?: boolean; pinned?: boolean }>({});

  const taskId = task?.id ?? null;
  const selectedActivityId = initialActivityId;
  const selectedActivityRef = React.useRef<HTMLDivElement | null>(null);
  const fetchEnabled = Boolean(isOpen && taskId !== null);

  const detailQuery = useQuery({
    queryKey: ['task', taskId, 'detail'],
    queryFn: () => tasksService.getTask(taskId!),
    enabled: fetchEnabled,
    staleTime: 30 * 1000,
    // Когда постановщик открыт на вкладке «Проверка», подтягиваем новые сдачи без перезагрузки страницы.
    // Также обновляем «Сдать задачу», чтобы исполнитель сразу видел "на доработку" и мог пересдать.
    refetchInterval: ['review', 'submit', 'deadline', 'results'].includes(activeTab) ? 5000 : false,
    refetchIntervalInBackground: false,
  });

  const activityQuery = useQuery({
    queryKey: ['task', taskId, 'activity', selectedActivityId],
    queryFn: () => tasksService.getTaskActivity(taskId!, selectedActivityId),
    enabled: fetchEnabled,
    staleTime: selectedActivityId ? 0 : 30 * 1000,
  });

  useEffect(() => {
    if (isOpen && activeTab === 'history' && selectedActivityId && activityQuery.data) {
      selectedActivityRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, activeTab, selectedActivityId, activityQuery.data]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const id = window.setInterval(() => setDeadlineTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, [isOpen]);

  useEffect(() => {
    setModalOpt({});
  }, [taskId, isOpen]);

  const currentUserId = useMemo(() => getCurrentUserId(), []);

  const detail = detailQuery.data;
  const trackedTaskId = React.useRef<number | null>(null);
  useEffect(() => {
    if (!isOpen) { trackedTaskId.current = null; return; }
    if (!detail?.id || trackedTaskId.current === detail.id) return;
    trackedTaskId.current = detail.id;
    void userActivityService.trackPage(`/tasks?openTaskId=${detail.id}`).catch(() => {});
  }, [isOpen, detail?.id]);

  const myPerformerRow = useMemo(() => {
    if (!detail || !currentUserId) return null;
    return detail.performers?.find((x) => x.id === currentUserId) ?? null;
  }, [detail, currentUserId]);

  const myPerformerAcceptedAt = myPerformerRow?.accepted_at ?? null;

  const iamPerformer = useMemo(
    () => Boolean(detail && currentUserId && detail.performers?.some((p) => p.id === currentUserId)),
    [detail, currentUserId],
  );

  const taskAllowsPerformerActions = useMemo(
    () => Boolean(detail && !['completed', 'cancelled'].includes(detail.status)),
    [detail],
  );

  const canAcceptAsPerformer = Boolean(
    iamPerformer && !myPerformerAcceptedAt && taskAllowsPerformerActions,
  );

  const canShowSubmitSidebar = Boolean(
    iamPerformer && myPerformerAcceptedAt && taskAllowsPerformerActions,
  );

  const submittedAt = myPerformerRow?.submitted_at ?? null;
  const reviewOutcome = myPerformerRow?.review_outcome ?? null;
  const waitingReviewerDecision = Boolean(submittedAt && reviewOutcome === null);
  const canSubmitWorkNow = Boolean(
    canShowSubmitSidebar && (!submittedAt || reviewOutcome === 'needs_revision'),
  );

  const viewerIsAssigner = Boolean(detail?.viewer_is_assigner);
  const taskStatus = detail?.status ?? '';
  /** Как на карточке задачи: постановщик может убрать задачу в архив */
  const canArchiveTask =
    viewerIsAssigner && taskStatus !== 'completed' && taskStatus !== 'cancelled';
  // Постановщик может завершить свою задачу даже без права task_complete
  const canAssignerComplete = Boolean(detail?.can_assigner_complete && viewerIsAssigner);

  const pendingReviews = detail?.pending_reviewer_actions ?? [];

  useEffect(() => {
    if (!isOpen || taskId == null) return;
    setActiveTab(initialTab);
  }, [isOpen, taskId, initialTab]);

  useEffect(() => {
    if (!isOpen || !detail) return;
    setActiveTab((current) => {
      if (current === 'submit' && (!canShowSubmitSidebar || !canSubmitWorkNow)) return 'overview';
      if (current === 'review' && !viewerIsAssigner) return 'overview';
      return current;
    });
  }, [isOpen, detail?.id, activeTab, canShowSubmitSidebar, canSubmitWorkNow, viewerIsAssigner, detail]);

  useEffect(() => {
    if (!isOpen || !taskId) return;
    setReviewCommentDraft('');
    setReviewBusy(null);
    setCompleteConfirmOpen(false);
    setUploadedFiles([]);
    setWorkDescription('');
    setSubmitWorkError(null);
  }, [isOpen, taskId]);

  useEffect(() => {
    if (!isOpen) {
      setCompleteConfirmOpen(false);
    }
  }, [isOpen]);

  const showDetailPreloader = fetchEnabled && !detail && !detailQuery.isError;
  const showDetailError = fetchEnabled && !detail && detailQuery.isError;
  const detailErrorStatus = (detailQuery.error as { response?: { status?: number } } | null)?.response?.status;
  const detailErrorMessage =
    detailErrorStatus === 403
      ? 'Нет доступа к этой задаче.'
      : detailErrorStatus === 404
        ? 'Задача не найдена.'
        : 'Не удалось загрузить задачу. Проверьте соединение и попробуйте снова.';

  const resolvedClientName = detail?.client?.name ?? clientName;
  const resolvedClientId = detail?.client_id ?? clientId;
  const resolvedProjectName = detail?.project?.name ?? projectName;
  const resolvedProjectId = detail?.project?.id ?? projectId;
  const resolvedCaseName = detail?.case?.name ?? caseName;
  const resolvedCaseIdentifier = detail?.case?.identifier ?? caseIdentifier;
  const resolvedCaseId = detail?.case?.id ?? caseId;

  const assigner: Participant = useMemo(() => {
    if (detail?.assigner) {
      return {
        id: detail.assigner.id,
        name: detail.assigner.name,
        avatar: detail.assigner.avatar || '/avatar.png',
      };
    }
    return {
      id: 0,
      name: task?.assigner || '—',
      avatar: '/avatar.png',
    };
  }, [detail?.assigner, task?.assigner]);

  const executors: Participant[] = useMemo(() => {
    if (detail?.performers?.length) {
      return detail.performers.map((p) => ({
        id: p.id,
        name: p.name,
        avatar: p.avatar || '/avatar.png',
      }));
    }
    if (task?.executor) {
      return [{ id: 0, name: task.executor, avatar: '/avatar.png' }];
    }
    return [];
  }, [detail?.performers, task?.executor]);

  const observers: Participant[] = useMemo(() => {
    if (!detail) return [];
    return (detail.observers ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      avatar: p.avatar || '/avatar.png',
    }));
  }, [detail]);

  const historyEntries: HistoryEntry[] = useMemo(() => {
    const rows: TaskActivityItem[] = activityQuery.data ?? [];
    return rows.map((row) => ({
      id: row.id,
      author: {
        id: row.user.id ?? row.id,
        name: row.user.name,
        avatar: row.user.avatar || '/avatar.png',
      },
      status: row.summary,
      changes: readFieldChanges(row.meta),
      comment: row.comment ?? undefined,
      date: row.date,
      time: row.time,
    }));
  }, [activityQuery.data]);

  const deadlineHeadline = useMemo(
    () => formatDeadlineCountdown(viewerIsAssigner && pendingReviews.length > 0 && detail?.review_deadline
      ? detail.review_deadline : effectiveDeadlineIsoFromDetail(detail), detail?.status ?? ''),
    [detail, deadlineTick, viewerIsAssigner, pendingReviews.length],
  );

  const headerIsFavorite = modalOpt.favorite ?? detail?.is_favorite ?? false;
  const headerIsPinned = modalOpt.pinned ?? detail?.is_pinned ?? false;

  const toggleModalFavorite = async () => {
    if (taskId == null) return;
    const currentEffective = modalOpt.favorite ?? detail?.is_favorite ?? false;
    const next = !currentEffective;
    setModalOpt((prev) => ({ ...prev, favorite: next }));
    try {
      await tasksService.toggleFavorite(taskId);
      await invalidateTaskRelatedQueries(queryClient);
    } catch (e: any) {
      setModalOpt((prev) => ({ ...prev, favorite: currentEffective }));
      alert(e?.response?.data?.message ?? e?.message ?? 'Ошибка');
    }
  };

  const toggleModalPin = async () => {
    if (taskId == null) return;
    const currentEffective = modalOpt.pinned ?? detail?.is_pinned ?? false;
    const next = !currentEffective;
    setModalOpt((prev) => ({ ...prev, pinned: next }));
    try {
      await tasksService.togglePinned(taskId);
      await invalidateTaskRelatedQueries(queryClient);
    } catch (e: any) {
      setModalOpt((prev) => ({ ...prev, pinned: currentEffective }));
      alert(e?.response?.data?.message ?? e?.message ?? 'Ошибка');
    }
  };

  const goEditTask = () => {
    if (!canEditTask) {
      alert('Недостаточно прав');
      return;
    }
    if (taskId == null) return;
    navigate(`/tasks/${taskId}/edit`);
    onClose();
  };

  const handleArchiveTask = async () => {
    if (!canArchiveTask || taskId == null) return;
    setArchiveSending(true);
    try {
      await tasksService.updateTask(taskId, { status: 'completed' });
      await invalidateTaskRelatedQueries(queryClient);
      setArchiveConfirmOpen(false);
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? e?.message ?? 'Не удалось перевести задачу в архив');
    } finally {
      setArchiveSending(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!canEditTask) {
      alert('Недостаточно прав');
      return;
    }
    if (taskId == null) return;
    setDeleteSending(true);
    try {
      await tasksService.deleteTask(taskId);
      await invalidateTaskRelatedQueries(queryClient);
      setDeleteConfirmOpen(false);
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? e?.message ?? 'Не удалось удалить задачу');
    } finally {
      setDeleteSending(false);
    }
  };

  const handleAcceptInModal = async () => {
    if (taskId == null) return;
    setAcceptingTask(true);
    try {
      await tasksService.acceptAsPerformer(taskId);
      await invalidateTaskRelatedQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['task', taskId], exact: false });
    } catch (e: any) {
      alert(e?.response?.data?.message ?? e?.message ?? 'Не удалось принять задачу');
    } finally {
      setAcceptingTask(false);
    }
  };

  const handleSubmitWork = async () => {
    if (taskId == null || !canSubmitWorkNow) return;
    const text = workDescription.trim();
    if (text === '' && uploadedFiles.length === 0) {
      setSubmitWorkError('Укажите описание работы или прикрепите файл');
      return;
    }
    setSubmitSending(true);
    setSubmitWorkError(null);
    try {
      const updated = await tasksService.submitTaskWork(taskId, { comment: text || undefined, files: uploadedFiles });
      queryClient.setQueryData(['task', taskId, 'detail'], updated);
      setActiveTab('results');
      setWorkDescription('');
      setUploadedFiles([]);
      await invalidateTaskRelatedQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['task', taskId], exact: false });
      await queryClient.invalidateQueries({ queryKey: ['task', taskId, 'activity'] });
    } catch (e: any) {
      setSubmitWorkError(e?.response?.data?.message ?? e?.message ?? 'Не удалось сдать задачу');
    } finally {
      setSubmitSending(false);
    }
  };

  const handleReviewPerformer = async (performerId: number, decision: 'accept' | 'needs_revision') => {
    if (taskId == null) return;
    setReviewBusy({ performerId, action: decision });
    try {
      await tasksService.reviewPerformerWork(taskId, {
        performer_id: performerId,
        decision,
        comment: reviewCommentDraft.trim() || undefined,
        submission_version: pendingReviews.find((p) => Number(p.performer_id) === performerId)?.submission_version ?? 0,
      });
      setReviewCommentDraft('');
      await invalidateTaskRelatedQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['task', taskId], exact: false });
      await queryClient.invalidateQueries({ queryKey: ['task', taskId, 'activity'] });
    } catch (e: any) {
      alert(e?.response?.data?.message ?? e?.message ?? 'Не удалось сохранить решение');
    } finally {
      setReviewBusy(null);
    }
  };

  const handleConfirmCompleteTask = async () => {
    if (taskId == null) return;
    setCompleteSending(true);
    try {
      await tasksService.completeTask(taskId);
      await invalidateTaskRelatedQueries(queryClient);
      setCompleteConfirmOpen(false);
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? e?.message ?? 'Не удалось завершить задачу');
    } finally {
      setCompleteSending(false);
    }
  };

  if (!isOpen || !task) {
    return (
      <TaskCompleteConfirmModal
        isOpen={false}
        isLoading={false}
        onClose={() => {}}
        onConfirm={() => {}}
      />
    );
  }

  // Функция генерации и скачивания PDF отчета с использованием pdfmake
  const generateAndDownloadTaskReport = (
    title: string,
    assignmentDate: string,
    dueDate: string,
    completedLine: string,
    description: string,
    practice: string,
    privacy: string,
    tagsLine: string,
    taskNumericId: number,
    projectName: string,
    clientName: string,
    assigner: Participant,
    executors: Participant[],
    observers: Participant[],
    historyEntries: HistoryEntry[],
    caseName?: string,
    caseIdentifier?: string,
  ) => {
    const reportDate = new Date().toLocaleDateString('ru-RU');
    const reportTime = new Date().toLocaleTimeString('ru-RU');

    // Определение структуры документа
    const docDefinition: any = {
      content: [
        { text: 'ОТЧЕТ ПО ЗАДАЧЕ', style: 'header' },
        
        { text: `ЗАГОЛОВОК: ${title}`, style: 'subheader' },
        
        { text: 'ПАРАМЕТРЫ', style: 'sectionHeader' },
        {
          ul: [
            { text: [`Поставлена: `, { text: assignmentDate, bold: false }], bold: true },
            { text: [`Выполнить до: `, { text: dueDate, bold: false }], bold: true },
            ...(detail?.review_deadline_formatted ? [{ text: [`Проверить до: `, { text: detail.review_deadline_formatted, bold: false }], bold: true }] : []),
            { text: [`Выполнена: `, { text: completedLine, bold: false }], bold: true },
          ]
        },
        
        { text: 'ИНФОРМАЦИЯ', style: 'sectionHeader' },
        {
          ul: [
            { text: [`Клиент: `, { text: clientName, bold: false }], bold: true },
            { text: [`Проект: `, { text: projectName, bold: false }], bold: true },
            ...(caseName ? [{ text: [`Дело: `, { text: `${caseName}${caseIdentifier ? ` (${caseIdentifier})` : ''}`, bold: false }], bold: true }] : []),
            { text: [`Практика: `, { text: practice, bold: false }], bold: true },
            { text: [`Приватность: `, { text: privacy, bold: false }], bold: true },
            { text: [`Теги: `, { text: tagsLine, bold: false }], bold: true },
          ]
        },
        
        { text: 'ОПИСАНИЕ ЗАДАЧИ', style: 'sectionHeader' },
        { text: description || '—', margin: [0, 0, 0, 10] },
        
        { text: 'УЧАСТНИКИ', style: 'sectionHeader' },
        { text: 'Постановщик:', style: 'subsectionHeader' },
        { ul: [`${assigner.name}`], margin: [0, 0, 0, 5] },
        
        { text: 'Исполнители:', style: 'subsectionHeader' },
        { ul: executors.length > 0 ? executors.map((e) => e.name) : ['—'], margin: [0, 0, 0, 5] },
        
        { text: 'Наблюдатели:', style: 'subsectionHeader' },
        { ul: observers.length > 0 ? observers.map((o) => o.name) : ['—'], margin: [0, 0, 0, 10] },
        
        { text: 'КЛЮЧЕВЫЕ СОБЫТИЯ ИСТОРИИ', style: 'sectionHeader' },
        ...historyEntries.map(entry => ({
          stack: [
            { text: `${entry.date} ${entry.time} - ${entry.author.name}`, bold: true },
            { text: `Статус: ${entry.status}` },
            ...(entry.comment ? [{ text: `Комментарий: ${entry.comment}` }] : []),
          ],
          margin: [0, 0, 0, 10]
        })),
        
        { text: `Дата формирования отчета: ${reportDate} ${reportTime}`, style: 'footer' }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          alignment: 'center',
          margin: [0, 0, 0, 20]
        },
        subheader: {
          fontSize: 14,
          bold: true,
          margin: [0, 0, 0, 15]
        },
        sectionHeader: {
          fontSize: 12,
          bold: true,
          margin: [0, 15, 0, 5],
          decoration: 'underline'
        },
        subsectionHeader: {
          fontSize: 11,
          bold: true,
          margin: [0, 5, 0, 2]
        },
        footer: {
          fontSize: 10,
          alignment: 'right',
          margin: [0, 30, 0, 0],
          color: 'gray'
        }
      },
      defaultStyle: {
        fontSize: 10
      }
    };

    // Генерация и скачивание PDF
    pdfMake.createPdf(docDefinition).download(`Отчет_задача_${taskNumericId}.pdf`);
  };

  const displayTitle = detail?.title ?? task.name;
  const overviewAssignment = (() => {
    const iso = detail?.created_at ?? null;
    if (iso) {
      const d = new Date(iso);
      if (!Number.isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
      }
    }
    return detail?.created_at_formatted ?? task.assignmentDate;
  })();
  const overviewDue =
    (detail?.deadline_formatted && detail.deadline_formatted.trim()) ||
    (detail?.due_date_display && detail.due_date_display.replace(/^До\s+/i, '').trim()) ||
    (task.dueDate && String(task.dueDate).trim()) ||
    '';
  const overviewCompleted =
    detail?.status === 'completed'
      ? detail.updated_at_formatted ?? '—'
      : '—';
  const overviewDescription = (detail?.description ?? '').trim();
  const overviewTags = (detail?.tags ?? []).join(', ') || '—';
  const overviewPractice =
    Array.isArray((detail as any)?.practice_areas) && (detail as any).practice_areas.length > 0
      ? (detail as any).practice_areas.join(', ')
      : (detail?.practice_area?.trim() || '—');
  const isTaskPrivate = Boolean(detail?.is_private);
  const overviewPrivacy = isTaskPrivate ? 'Приватная' : '';
  const overviewPrivacyForPdf = isTaskPrivate ? 'Приватная' : 'Общедоступная';

  const modalContent = (
    <div className="task-modal-overlay" onClick={onClose}>
      <div className="task-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Верхняя полоска */}
        <div className="task-modal-top-bar"></div>
        {/* Заголовок */}
        <div className="task-modal-header">
          <h2 className="task-modal-title">{displayTitle}</h2>
          <div className="task-modal-deadline">
            <img src="/icons/modals/TaskModal/time1-svgrepo-com-1.svg" alt="Время" className="task-modal-deadline-icon" />
            <div className="task-modal-deadline-content">
              <span className="task-modal-deadline-label">{viewerIsAssigner && pendingReviews.length > 0 && detail?.review_deadline ? 'До конца срока проверки:' : 'До конца срока выполнения:'}</span>
              <span className="task-modal-deadline-time">{deadlineHeadline}</span>
            </div>
          </div>
          <div className="task-modal-header-actions">
            {canArchiveTask && (
              <button
                type="button"
                className="task-modal-header-action-btn"
                onClick={() => setArchiveConfirmOpen(true)}
                title="Перевести в архив"
                disabled={archiveSending}
              >
                <img src="/icons/task/delete-archive.svg" alt="" />
              </button>
            )}
            {canEditTask && (
              <button type="button" className="task-modal-header-action-btn" onClick={goEditTask} title="Редактировать">
                <img src="/icons/task/edit.svg" alt="" />
              </button>
            )}
            <button
              type="button"
              className="task-modal-header-action-btn"
              onClick={() => void toggleModalFavorite()}
              title={headerIsFavorite ? 'Убрать из избранного' : 'В избранное'}
            >
              <img src={headerIsFavorite ? '/icons/projects/star-gold.svg' : '/icons/projects/Star.svg'} alt="" />
            </button>
            <button
              type="button"
              className="task-modal-header-action-btn"
              onClick={() => void toggleModalPin()}
              title={headerIsPinned ? 'Открепить' : 'Закрепить'}
            >
              <img src={headerIsPinned ? '/icons/projects/PushPin-activ.svg' : '/icons/projects/PushPin.svg'} alt="" />
            </button>
            {canEditTask && (
              <button
                type="button"
                className="task-modal-header-action-btn task-modal-header-action-btn--danger"
                onClick={() => setDeleteConfirmOpen(true)}
                title="Удалить задачу"
                disabled={deleteSending}
              >
                <img src="/icons/clients/trash.svg" alt="" />
              </button>
            )}
          </div>
          <button type="button" className="task-modal-close" onClick={onClose}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="20" cy="20" r="20" fill="#084B7A"/>
              <path d="M13 13L27 27M27 13L13 27" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="task-modal-body">
          {/* Боковое меню */}
          <div className="task-modal-sidebar">
            <div 
              className={`task-modal-sidebar-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <img src="/icons/modals/TaskModal/Info.svg" alt="Обзор задачи" className="task-modal-sidebar-icon" />
              <span>Обзор задачи</span>
            </div>
            <button
              type="button"
              className={`task-modal-sidebar-item ${activeTab === 'subtasks' ? 'active' : ''}`}
              aria-pressed={activeTab === 'subtasks'}
              onClick={() => setActiveTab('subtasks')}
            >
              <img src="/icons/modals/TaskModal/Page.svg" alt="" className="task-modal-sidebar-icon" />
              <span>Подзадачи</span>
            </button>
            <div 
              className={`task-modal-sidebar-item ${activeTab === 'participants' ? 'active' : ''}`}
              onClick={() => setActiveTab('participants')}
            >
              <img src="/icons/modals/TaskModal/User-1.svg" alt="Участники" className="task-modal-sidebar-icon" />
              <span>Участники</span>
            </div>
            <div 
              className={`task-modal-sidebar-item ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <img src="/icons/modals/TaskModal/Page.svg" alt="История" className="task-modal-sidebar-icon" />
              <span>История</span>
            </div>
            {viewerIsAssigner && (
              <div
                className={`task-modal-sidebar-item ${activeTab === 'review' ? 'active' : ''}`}
                onClick={() => setActiveTab('review')}
              >
                <img src="/icons/modals/TaskModal/Check.svg" alt="Проверка" className="task-modal-sidebar-icon" />
                <span>Проверка</span>
              </div>
            )}
            {(iamPerformer || viewerIsAssigner || Boolean(detail?.deadline_requests?.length)) && (
              <button type="button" className={`task-modal-sidebar-item ${activeTab === 'deadline' ? 'active' : ''}`} onClick={() => setActiveTab('deadline')}>
                <img src="/icons/modals/TaskModal/time1-svgrepo-com-1.svg" alt="" className="task-modal-sidebar-icon" />
                <span>Перенос срока{viewerIsAssigner && detail?.deadline_requests?.some((r) => r.status === 'pending') ? ' •' : ''}</span>
              </button>
            )}
            {detail?.performers.some((p) => p.submitted_at) && (
              <button type="button" className={`task-modal-sidebar-item ${activeTab === 'results' ? 'active' : ''}`} onClick={() => setActiveTab('results')}>
                <img src="/icons/modals/TaskModal/Check.svg" alt="" className="task-modal-sidebar-icon" />
                <span>Результаты работы{viewerIsAssigner && detail.performers.some((p) => p.review_outcome !== 'needs_revision' && p.work_edit_request?.status === 'pending') ? ' •' : ''}</span>
              </button>
            )}
            <div 
              className="task-modal-sidebar-item"
              onClick={() => {
                generateAndDownloadTaskReport(
                  displayTitle,
                  overviewAssignment,
                  overviewDue,
                  overviewCompleted,
                  overviewDescription,
                  overviewPractice,
                  overviewPrivacyForPdf,
                  overviewTags,
                  taskId ?? task.id,
                  resolvedProjectName,
                  resolvedClientName,
                  assigner,
                  executors,
                  observers,
                  historyEntries,
                  resolvedCaseName || undefined,
                  resolvedCaseIdentifier || undefined,
                );
              }}
            >
              <img src="/icons/modals/TaskModal/FilePdf.svg" alt="Скачать отчет" className="task-modal-sidebar-icon" />
              <span>Скачать отчет</span>
            </div>
            {(canAcceptAsPerformer || canSubmitWorkNow || canAssignerComplete) && (
              <>
                <div className="task-modal-sidebar-divider"></div>
                {canAcceptAsPerformer && (
                  <div
                    className={`task-modal-sidebar-item task-modal-submit-item${acceptingTask ? ' task-modal-sidebar-item--busy' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (acceptingTask) return;
                      void handleAcceptInModal();
                    }}
                    onKeyDown={(e) => {
                      if (acceptingTask) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void handleAcceptInModal();
                      }
                    }}
                  >
                    <img src="/icons/task/galka.svg" alt="" className="task-modal-sidebar-icon" />
                    <span>{acceptingTask ? 'Принимаем…' : 'Принять задачу'}</span>
                  </div>
                )}
                {canSubmitWorkNow && (
                  <div
                    className={`task-modal-sidebar-item task-modal-submit-item ${activeTab === 'submit' ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveTab('submit')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveTab('submit');
                      }
                    }}
                  >
                    <img src="/icons/modals/TaskModal/Check.svg" alt="" className="task-modal-sidebar-icon" />
                    <span>Сдать задачу</span>
                  </div>
                )}
                {canAssignerComplete && (
                  <div
                    className="task-modal-sidebar-item task-modal-submit-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => setCompleteConfirmOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setCompleteConfirmOpen(true);
                      }
                    }}
                  >
                    <img src="/icons/modals/TaskModal/Check.svg" alt="" className="task-modal-sidebar-icon" />
                    <span>Завершить</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Основной контент */}
          <div className="task-modal-main">
            {showDetailPreloader && (
              <div className="task-modal-main-loader" aria-busy="true" aria-live="polite">
                <PageLoader text="Загрузка задачи…" />
              </div>
            )}
            {showDetailError && (
              <div className="task-modal-main-loader task-modal-main-loader--error">
                <p className="task-modal-main-error-text">{detailErrorMessage}</p>
              </div>
            )}
            {detail && activeTab === 'overview' && (
              <>
                <div className="task-modal-info">
                  <div className="task-modal-info-row">
                    <div className="task-modal-info-item">
                      <span className="task-modal-info-label task-modal-info-label-date">Поставлена:</span>
                      <span className="task-modal-info-value task-modal-info-value-date">{overviewAssignment || '—'}</span>
                    </div>
                    <div className="task-modal-info-item">
                      <span className="task-modal-info-label task-modal-info-label-date">Выполнить до:</span>
                      <span className="task-modal-info-value task-modal-info-value-date">{overviewDue || '—'}</span>
                    </div>
                    {detail.review_deadline_formatted && <div className="task-modal-info-item">
                      <span className="task-modal-info-label task-modal-info-label-date">Проверить до:</span>
                      <span className={`task-modal-info-value task-modal-info-value-date${detail.is_review_overdue ? ' task-modal-review-overdue' : ''}`}>
                        {detail.review_deadline_formatted}{detail.is_review_overdue ? ' · Срок проверки истёк' : ''}
                      </span>
                    </div>}
                    <div className="task-modal-info-item">
                      <span className="task-modal-info-label task-modal-info-label-date">Выполнена:</span>
                      <span className="task-modal-info-value task-modal-info-value-date">{overviewCompleted}</span>
                    </div>
                  </div>

                  <div className="task-modal-info-row">
                    <div className="task-modal-info-item">
                      <span className="task-modal-info-label">Клиент:</span>
                      {resolvedClientId ? (
                        <Link
                          to={`/clients/${resolvedClientId}`}
                          className="task-modal-info-value task-modal-link"
                          onClick={onClose}
                        >
                          {resolvedClientName || '—'}
                          <img src="/icons/projects/ArrowCircleUpRight.svg" alt="Перейти" className="task-modal-link-icon" />
                        </Link>
                      ) : (
                        <span className="task-modal-info-value">{resolvedClientName || '—'}</span>
                      )}
                    </div>
                    <div className="task-modal-info-item">
                      <span className="task-modal-info-label">Проект:</span>
                      {resolvedProjectId ? (
                        <Link
                          to={`/projects/${resolvedProjectId}`}
                          className="task-modal-info-value task-modal-link"
                          onClick={onClose}
                        >
                          {resolvedProjectName || '—'}
                          <img src="/icons/projects/ArrowCircleUpRight.svg" alt="Перейти" className="task-modal-link-icon" />
                        </Link>
                      ) : (
                        <span className="task-modal-info-value">{resolvedProjectName || '—'}</span>
                      )}
                    </div>
                    {(resolvedCaseName || resolvedCaseIdentifier) && (
                      <div className="task-modal-info-item">
                        <span className="task-modal-info-label">Дело:</span>
                        {resolvedCaseId ? (
                          <Link
                            to={`/cases/${resolvedCaseId}`}
                            className="task-modal-info-value task-modal-case-value task-modal-link"
                            onClick={onClose}
                          >
                            {resolvedCaseName ? (
                              <span className="task-modal-case-name">{resolvedCaseName}</span>
                            ) : null}
                            {resolvedCaseIdentifier ? (
                              <span className="task-modal-case-identifier">({resolvedCaseIdentifier})</span>
                            ) : null}
                            <img src="/icons/projects/ArrowCircleUpRight.svg" alt="Перейти" className="task-modal-link-icon" />
                          </Link>
                        ) : (
                          <div className="task-modal-info-value task-modal-case-value">
                            {resolvedCaseName ? (
                              <span className="task-modal-case-name">{resolvedCaseName}</span>
                            ) : null}
                            {resolvedCaseIdentifier ? (
                              <span className="task-modal-case-identifier">({resolvedCaseIdentifier})</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="task-modal-info-item">
                      <span className="task-modal-info-label">Практика:</span>
                      <span className="task-modal-info-value">{overviewPractice}</span>
                    </div>
                  </div>

                  <div className="task-modal-info-row">
                    <div className="task-modal-info-item">
                      <span className="task-modal-info-label">Теги:</span>
                      <span className="task-modal-info-value">{overviewTags}</span>
                    </div>
                    <div className="task-modal-info-item">
                      <span className="task-modal-info-label">Приоритет:</span>
                      <span className="task-modal-info-value">
                        {detail ? priorityLabel(detail.priority) : '—'}
                      </span>
                    </div>
                    {isTaskPrivate && (
                      <div className="task-modal-info-item">
                        <span className="task-modal-info-label">Приватность:</span>
                        <span className="task-modal-info-value">{overviewPrivacy}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="task-modal-description">
                  <h3 className="task-modal-section-title">Описание задачи:</h3>
                  {overviewDescription ? (
                    <p style={{ whiteSpace: 'pre-wrap' }}>{overviewDescription}</p>
                  ) : (
                    <p>Описание не указано.</p>
                  )}
                </div>

                <div className="task-modal-documents">
                  <h3 className="task-modal-section-title">Документы по задаче:</h3>
                  {(detail?.attachments?.length ?? 0) > 0 ? (
                    <div className="task-modal-documents-list">
                      {detail!.attachments!.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className="task-modal-document"
                          onClick={() => {
                            if (!detail?.id) return;
                            void tasksService.downloadTaskAttachment(detail.id, a.id, a.name);
                          }}
                        >
                          <div className="task-modal-document-icon">
                            <img src="/icons/modals/TaskModal/file.svg" alt="" />
                          </div>
                          <span>{a.name}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="task-modal-info-value" style={{ fontSize: 14 }}>
                      Нет прикреплённых файлов
                    </p>
                  )}
                </div>

              </>
            )}

            {detail && activeTab === 'subtasks' && (
              <TaskSubtasks key={detail.id} task={detail} />
            )}

            {detail && activeTab === 'participants' && (
              <div className="task-modal-participants">
                <div className="task-modal-participants-section">
                  <h3 className="task-modal-participants-title">Постановщик:</h3>
                  <div className="task-modal-participant">
                    <img src={assigner.avatar || '/avatar.png'} alt={assigner.name} className="task-modal-participant-avatar" />
                    <span className="task-modal-participant-name">{assigner.name}</span>
                  </div>
                </div>

                <div className="task-modal-participants-section">
                  <h3 className="task-modal-participants-title">Исполнители:</h3>
                  <div className="task-modal-participants-list">
                    {executors.length === 0 ? (
                      <span className="task-modal-participant-name">Не назначены</span>
                    ) : (
                      executors.map((executor, idx) => (
                        <div key={`${executor.id}-${idx}`} className="task-modal-participant">
                          <img src={executor.avatar || '/avatar.png'} alt={executor.name} className="task-modal-participant-avatar" />
                          <span className="task-modal-participant-name">{executor.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {observers.length > 0 && (
                  <div className="task-modal-participants-section">
                    <h3 className="task-modal-participants-title">Наблюдатели:</h3>
                    <div className="task-modal-participants-list">
                      {observers.map((observer, idx) => (
                        <div key={`${observer.id}-${idx}`} className="task-modal-participant">
                          <img
                            src={observer.avatar || '/avatar.png'}
                            alt={observer.name}
                            className="task-modal-participant-avatar"
                          />
                          <span className="task-modal-participant-name">{observer.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {detail && activeTab === 'history' && (
              <div className="task-modal-history">
                {activityQuery.isLoading && (
                  <p className="task-modal-participant-name">Загрузка истории…</p>
                )}
                {activityQuery.isError && (
                  <p className="task-modal-participant-name" style={{ color: '#B91C1C' }}>
                    Не удалось загрузить историю.
                  </p>
                )}
                {!activityQuery.isLoading &&
                  !activityQuery.isError &&
                  historyEntries.length === 0 && (
                    <p className="task-modal-participant-name">
                      Пока нет записей. Здесь отображаются создание задачи и смена статуса.
                    </p>
                  )}
                {!activityQuery.isLoading && historyEntries.length > 0 && (
                  <div className="task-modal-history-timeline">
                    <div className="task-modal-history-line-vertical"></div>
                    {historyEntries.map((entry) => (
                      <div key={entry.id} ref={entry.id === selectedActivityId ? selectedActivityRef : undefined} className={`task-modal-history-entry${entry.id === selectedActivityId ? ' activity-change-selected' : ''}`}>
                        <div className="task-modal-history-dot-wrapper">
                          <div className="task-modal-history-dot"></div>
                        </div>
                        <div className="task-modal-history-content">
                          <div className="task-modal-history-row">
                            <div className="task-modal-history-author">
                              <img src={entry.author.avatar || '/avatar.png'} alt={entry.author.name} className="task-modal-history-avatar" />
                              <span className="task-modal-history-author-name">{entry.author.name}</span>
                            </div>
                            <div className="task-modal-history-status">Событие: {entry.status}</div>
                            <div className="task-modal-history-time">
                              <div className="task-modal-history-date">
                                <img src="/icons/projects/CalendarCheck.svg" alt="Календарь" className="task-modal-history-calendar-icon" />
                                <span>{entry.date}</span>
                              </div>
                              <div className="task-modal-history-time-value">
                                <img src="/icons/workplace/time.svg" alt="Время" className="task-modal-history-clock-icon" />
                                <span>{entry.time}</span>
                              </div>
                            </div>
                          </div>
                          {entry.comment && (
                            <div className="task-modal-history-comment">{entry.comment}</div>
                          )}
                          {entry.changes && entry.changes.length > 0 && <ChangeDetails changes={entry.changes} />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {detail && activeTab === 'deadline' && (
              <TaskDeadlineRequests key={detail.id} task={detail} viewerId={currentUserId} onChanged={async (updated) => {
                queryClient.setQueryData(['task', taskId, 'detail'], updated);
                await invalidateTaskRelatedQueries(queryClient);
                await queryClient.invalidateQueries({ queryKey: ['notifications'] });
              }} />
            )}

            {detail && activeTab === 'review' && (
              <div className="task-modal-review-panel">
                {detail?.review_deadline_formatted && <p className={detail.is_review_overdue ? 'task-modal-review-overdue' : ''}>
                  Проверить до: {detail.review_deadline_formatted}{detail.is_review_overdue ? ' · Срок проверки истёк' : ''}
                </p>}
                {pendingReviews.length > 0 ? (
                  <>
                    <h3 className="task-modal-review-panel-title">Проверка работы исполнителей</h3>
                    <textarea
                      className="task-modal-review-comment-input"
                      placeholder="Комментарий проверяющего (при необходимости)"
                      value={reviewCommentDraft}
                      onChange={(e) => setReviewCommentDraft(e.target.value)}
                      rows={3}
                    />
                    <ul className="task-modal-review-list">
                      {pendingReviews.map((p) => {
                        const performerId = Number(p.performer_id);
                        const rowBusy =
                          reviewBusy !== null && reviewBusy.performerId === performerId;
                        return (
                          <li key={p.performer_id} className="task-modal-review-item">
                            <div className="task-modal-review-item-head">
                              <span className="task-modal-review-name">{p.name}</span>
                              {p.submission_comment ? (
                                <p className="task-modal-review-perf-comment">{p.submission_comment}</p>
                              ) : null}
                            </div>
                            <div className="task-modal-review-actions">
                              <button
                                type="button"
                                className="task-modal-review-btn task-modal-review-btn-accept"
                                disabled={rowBusy}
                                onClick={() => void handleReviewPerformer(performerId, 'accept')}
                              >
                                {rowBusy && reviewBusy?.action === 'accept'
                                  ? 'Принимаем…'
                                  : 'Принять работу'}
                              </button>
                              <button
                                type="button"
                                className="task-modal-review-btn task-modal-review-btn-revision"
                                disabled={rowBusy}
                                onClick={() => void handleReviewPerformer(performerId, 'needs_revision')}
                              >
                                {rowBusy && reviewBusy?.action === 'needs_revision'
                                  ? 'Отправка…'
                                  : 'На доработку'}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : (
                  <p className="task-modal-participant-name">
                    Сейчас нет работ, ожидающих проверки.
                  </p>
                )}
              </div>
            )}

            {detail && activeTab === 'results' && (
              <TaskWorkResults key={detail.id} task={detail} viewerId={currentUserId} onChanged={async (updated) => {
                queryClient.setQueryData(['task', taskId, 'detail'], updated);
                await invalidateTaskRelatedQueries(queryClient);
                await queryClient.invalidateQueries({ queryKey: ['notifications'] });
              }} />
            )}

            {detail &&
              activeTab === 'submit' &&
              (!canShowSubmitSidebar || waitingReviewerDecision || canSubmitWorkNow) && (
              <div className="task-modal-submit">
                {!canShowSubmitSidebar ? (
                  <p className="task-modal-submit-hint" style={{ fontSize: 15, color: '#084B7A', lineHeight: 1.5 }}>
                    {iamPerformer && !myPerformerAcceptedAt && taskAllowsPerformerActions
                      ? 'Чтобы сдать задачу, сначала примите её — пункт «Принять задачу» в меню слева или на карточке в списке.'
                      : 'Раздел «Сдать задачу» доступен только назначенным исполнителям после принятия задачи.'}
                  </p>
                ) : waitingReviewerDecision ? (
                  <p className="task-modal-submit-hint" style={{ fontSize: 15, color: '#084B7A', lineHeight: 1.5 }}>
                    Работа отправлена постановщику. Ожидайте проверки.
                  </p>
                ) : canSubmitWorkNow ? (
                  <>
                    <div className="task-modal-submit-section">
                      <h3 className="task-modal-submit-title">Описание проделанной работы</h3>
                      <textarea
                        className="task-modal-submit-textarea"
                        placeholder="Опишите проделанную работу или приложите только файлы"
                        value={workDescription}
                        onChange={(e) => {
                          setWorkDescription(e.target.value);
                          if (submitWorkError) setSubmitWorkError(null);
                        }}
                        rows={8}
                      />
                      {submitWorkError ? (
                        <p className="task-form-error" role="alert" style={{ color: '#b91c1c', marginTop: 8 }}>
                          {submitWorkError}
                        </p>
                      ) : null}
                    </div>

                    <div className="task-modal-submit-section">
                      <h3 className="task-modal-submit-title">Файлы (по желанию)</h3>
                      <label className="task-modal-submit-file-upload">
                        <input
                          type="file"
                          multiple
                          onChange={(e) => {
                            const picked = e.target.files;
                            if (picked?.length) {
                              const newFiles = Array.from(picked);
                              setUploadedFiles((prev) => [...prev, ...newFiles]);
                              if (submitWorkError) setSubmitWorkError(null);
                            }
                            e.target.value = '';
                          }}
                          style={{ display: 'none' }}
                        />
                        <div className="task-modal-submit-file-area">
                          <img src="/icons/modals/TaskModal/download-svgrepo-com-1.svg" alt="" className="task-modal-submit-file-icon" />
                          <span>Для загрузки файла кликните по полю.</span>
                        </div>
                      </label>
                      {uploadedFiles.length > 0 && (
                        <div className="task-modal-submit-files-list">
                          {uploadedFiles.map((file, index) => (
                            <div key={`${file.name}-${index}`} className="task-modal-submit-file-item">
                              <span>{file.name}</span>
                              <button
                                type="button"
                                className="task-modal-submit-file-remove"
                                onClick={() => {
                                  const newFiles = [...uploadedFiles];
                                  newFiles.splice(index, 1);
                                  setUploadedFiles(newFiles);
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="task-modal-submit-complete-btn"
                      disabled={submitSending}
                      onClick={() => void handleSubmitWork()}
                    >
                      <span>{submitSending ? 'Отправка…' : 'Сдать задачу'}</span>
                      <img src="/icons/modals/TaskModal/exchange-svgrepo-com-1.svg" alt="" className="task-modal-submit-arrow" />
                    </button>
                  </>
                ) : null}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      <TaskCompleteConfirmModal
        isOpen={isOpen && completeConfirmOpen}
        isLoading={completeSending}
        onClose={() => !completeSending && setCompleteConfirmOpen(false)}
        onConfirm={() => void handleConfirmCompleteTask()}
      />
      <DeleteConfirmModal
        isOpen={isOpen && archiveConfirmOpen}
        onClose={() => !archiveSending && setArchiveConfirmOpen(false)}
        onConfirm={() => void handleArchiveTask()}
        message={
          displayTitle
            ? `Вы уверены, что хотите перевести задачу «${displayTitle}» в архив?`
            : 'Вы уверены, что хотите перевести задачу в архив?'
        }
        confirmButtonText={archiveSending ? 'Перевод…' : 'В архив'}
      />
      <DeleteConfirmModal
        isOpen={isOpen && deleteConfirmOpen}
        onClose={() => !deleteSending && setDeleteConfirmOpen(false)}
        onConfirm={() => void handleDeleteTask()}
        message={
          displayTitle
            ? `Вы уверены, что хотите удалить задачу «${displayTitle}» безвозвратно?`
            : 'Вы уверены, что хотите удалить задачу безвозвратно?'
        }
        confirmButtonText={deleteSending ? 'Удаление…' : 'Удалить'}
      />
    </>
  );
};

export default TaskModal;
